"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import type { SimulationRoomStatus } from "./simulationRoomTypes"

/**
 * The elapsed-time clock for one simulation room session.
 *
 * Anchors to the server's timestamps (started_at/server_now from session
 * messages) and then runs on a monotonic clock, so wall-clock corrections and
 * replayed messages cannot move the displayed timer back.
 */
export function useSimulationRoomTimer(status: SimulationRoomStatus) {
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null)
  const timerAnchorRef = useRef<{
    elapsedAtAnchor: number
    monotonicAtAnchor: number
  } | null>(null)
  const latestSessionRef = useRef<{
    startedAt: number
    serverNow: number
  } | null>(null)

  const anchorTimer = useCallback(
    (sessionStartedAt: number, serverNow: number) => {
      const elapsedAtAnchor = Math.max(0, serverNow - sessionStartedAt)
      const previousAnchor = timerAnchorRef.current
      timerAnchorRef.current = {
        elapsedAtAnchor: Math.max(
          previousAnchor?.elapsedAtAnchor ?? 0,
          elapsedAtAnchor,
        ),
        monotonicAtAnchor: performance.now(),
      }
      setStartedAt(sessionStartedAt)
    },
    [],
  )

  useEffect(() => {
    if (
      startedAt === null ||
      timerAnchorRef.current === null ||
      status === "complete" ||
      status === "idle"
    ) {
      return
    }
    const update = () => {
      const anchor = timerAnchorRef.current
      if (anchor === null) {
        return
      }
      const nextElapsed =
        anchor.elapsedAtAnchor +
        (performance.now() - anchor.monotonicAtAnchor) / 1000
      setElapsedSeconds((current) => Math.max(current ?? 0, nextElapsed))
    }
    update()
    const handle = setInterval(() => {
      update()
    }, 250)
    return () => clearInterval(handle)
  }, [startedAt, status])

  const resetElapsed = useCallback(() => {
    setElapsedSeconds(null)
  }, [])

  return {
    elapsedSeconds,
    anchorTimer,
    resetElapsed,
    timerAnchorRef,
    latestSessionRef,
  }
}
